import { onMounted, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n, usePlanLimits, type PlanLimitsProp } from '@/composables';

type FlowVisitCapConditionEntry = {
  id: string;
  predicate: string;
  values: Record<string, number>;
};

type FlowConditions = Record<string, FlowVisitCapConditionEntry>;

export const useFlowVisitCapCondition = (conditions: FlowConditions) => {
  const { t } = useI18n();
  const planLimitsControl = usePlanLimits('visitCapCondition');

  const value = ref<Record<string, number>>({});
  const enabled = ref<Record<string, boolean>>({
    perHour: false,
    perDay: false,
    perMonth: false,
  });

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl.hasPlanLimitsRestriction(limitKey);

  const updateConditions = () => {
    const cleanedValue = Object.entries(value.value).reduce<Record<string, number>>(
      (accumulator, [key, entryValue]) => {
        if (entryValue !== 0 && enabled.value[key]) {
          accumulator[key] = entryValue;
        }
        return accumulator;
      },
      {},
    );
    conditions.visitCap.values = cleanedValue;
  };

  watch(
    () => conditions,
    () => {
      if (!conditions.visitCap) {
        conditions.visitCap = {
          id: uuidv4(),
          predicate: 'LOWER_OR_EQUALS',
          values: {},
        };
      }
    },
    { immediate: true },
  );

  watch(enabled, () => {
    updateConditions();
  }, { deep: true });

  watch(value, () => {
    updateConditions();
  }, { deep: true });

  onMounted(() => {
    value.value = { ...conditions.visitCap.values };
    Object.keys(value.value).forEach((item) => {
      enabled.value[item] = !!value.value[item];
    });
  });

  return {
    t,
    value,
    enabled,
    hasPlanLimitsRestriction,
  };
};
